import { useCallback, useEffect, useRef, useState } from "react";

const INTERACTIVE_SELECTOR =
  "button, input, textarea, select, a, [role='button'], label, .MuiCheckbox-root";

const DRAG_THRESHOLD = 3;

const getWindowScrollY = () =>
  window.scrollY || document.documentElement.scrollTop || 0;

const clearTextSelection = () => {
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    selection.removeAllRanges();
  }
};

const useDragScroll = ({ verticalScroll = "element" } = {}) => {
  const ref = useRef(null);
  const dragState = useRef({
    pending: false,
    active: false,
    startClientX: 0,
    startClientY: 0,
    scrollLeft: 0,
    scrollTop: 0,
    windowScrollY: 0,
  });
  const frameRef = useRef(null);
  const pendingMoveRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  const isInteractiveTarget = (target) => target.closest(INTERACTIVE_SELECTOR);

  const stopDragging = useCallback(() => {
    dragState.current.pending = false;
    dragState.current.active = false;
    pendingMoveRef.current = null;

    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    setIsDragging(false);
  }, []);

  const suppressNextClick = useCallback(() => {
    const preventClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      document.removeEventListener("click", preventClick, true);
    };

    document.addEventListener("click", preventClick, true);
    window.setTimeout(() => {
      document.removeEventListener("click", preventClick, true);
    }, 0);
  }, []);

  const applyScroll = useCallback(
    (event) => {
      const element = ref.current;
      if (!element) return;

      const {
        active,
        startClientX,
        startClientY,
        scrollLeft,
        scrollTop,
        windowScrollY,
      } = dragState.current;
      if (!active) return;

      const deltaX = event.clientX - startClientX;
      const deltaY = event.clientY - startClientY;

      element.scrollLeft = scrollLeft - deltaX;

      if (verticalScroll === "window") {
        window.scrollTo({
          top: windowScrollY - deltaY,
          left: window.scrollX,
        });
      } else {
        element.scrollTop = scrollTop - deltaY;
      }
    },
    [verticalScroll]
  );

  const scheduleScroll = useCallback(
    (event) => {
      pendingMoveRef.current = event;

      if (frameRef.current !== null) return;

      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        const pendingEvent = pendingMoveRef.current;
        if (pendingEvent) {
          applyScroll(pendingEvent);
        }
      });
    },
    [applyScroll]
  );

  const handleMouseDown = useCallback((event) => {
    if (event.button !== 0 || event.ctrlKey) return;

    const element = ref.current;
    if (!element || isInteractiveTarget(event.target)) return;

    dragState.current = {
      pending: true,
      active: false,
      startClientX: event.clientX,
      startClientY: event.clientY,
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop,
      windowScrollY: getWindowScrollY(),
    };
  }, []);

  const handleMouseMove = useCallback(
    (event) => {
      const element = ref.current;
      if (!element) return;

      if (event.ctrlKey) {
        if (dragState.current.pending || dragState.current.active) {
          stopDragging();
        }
        return;
      }

      const { pending, active, startClientX, startClientY } = dragState.current;
      if (!pending && !active) return;

      const deltaX = event.clientX - startClientX;
      const deltaY = event.clientY - startClientY;

      if (pending && !active) {
        if (Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) {
          if (Math.abs(deltaX) > 0 || Math.abs(deltaY) > 0) {
            event.preventDefault();
          }
          return;
        }

        dragState.current.pending = false;
        dragState.current.active = true;
        clearTextSelection();
        setIsDragging(true);
      }

      event.preventDefault();
      scheduleScroll(event);
    },
    [scheduleScroll, stopDragging]
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Control") {
        setIsSelectionMode(true);
      }
    };

    const onKeyUp = (event) => {
      if (event.key === "Control") {
        setIsSelectionMode(false);
      }
    };

    const onBlur = () => {
      setIsSelectionMode(false);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  useEffect(() => {
    const onMouseMove = (event) => handleMouseMove(event);
    const onMouseUp = () => {
      const wasDragging = dragState.current.active;
      stopDragging();

      if (wasDragging) {
        suppressNextClick();
      }
    };

    document.addEventListener("mousemove", onMouseMove, { passive: false });
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      stopDragging();
    };
  }, [handleMouseMove, stopDragging, suppressNextClick]);

  useEffect(() => {
    if (!isDragging) return undefined;

    const preventSelection = (event) => event.preventDefault();

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;

    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    document.addEventListener("selectstart", preventSelection);

    return () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      document.removeEventListener("selectstart", preventSelection);
      clearTextSelection();
    };
  }, [isDragging]);

  return {
    ref,
    isDragging,
    isSelectionMode,
    onMouseDown: handleMouseDown,
  };
};

export default useDragScroll;
